import { useEffect, useState } from "react";
//Styling
import "./WatchShowcase.css";

export const WatchShowcase = (props) => {
  const { detailData, type = "movie", season = null, episode = null } = props;
  const title = detailData.title;
  const tmdbId = detailData.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [availableEpisodes, setAvailableEpisodes] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [showVerifyInput, setShowVerifyInput] = useState(false);
  const [verifyInput, setVerifyInput] = useState("");

  useEffect(() => {
    const fetchStreamingLink = async () => {
      try {
        setLoading(true);
        setError(null);

        if (type === "movie") {
          const url = await fetchMovieStreamingLink(title, tmdbId);
          setEmbedUrl(url);
        } else if (type === "tv" && season && episode) {
          const { url, episodes } = await fetchTvStreamingLink(
            title,
            tmdbId,
            season,
            episode
          );
          setEmbedUrl(url);
          setAvailableEpisodes(episodes);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching streaming link:", err);
        setError(err.message || "Failed to load streaming link");
        setLoading(false);
      }
    };

    if (title && tmdbId) {
      fetchStreamingLink();
    }
  }, [title, tmdbId, type, season, episode]);

  const handleVerifyClick = () => {
    setShowVerifyInput(true);
  };

  const handleVerifyInputChange = (e) => {
    setVerifyInput(e.target.value);
  };

  const handleVerifySubmit = () => {
    if (verifyInput.toLowerCase() === "verify") {
      setIsVerified(true);
      setShowVerifyInput(false);
      setVerifyInput("");
    } else {
      alert("Incorrect verification. Please type 'Verify'");
      setVerifyInput("");
    }
  };

  return (
    <div className="watch_showcase_container">
      <div className="watch_showcase_wrapper">
        <div className="watch_showcase_title">Watch Now</div>
        {loading && <div className="watch_loading">Loading streaming link...</div>}
        {error && <div className="watch_error">Error: {error}</div>}

        {/* Verification Block */}
        {!loading && !error && !isVerified && embedUrl && (
          <div className="watch_verification_block">
            <div className="verification_content">
              {!showVerifyInput ? (
                <button
                  className="verify_button"
                  onClick={handleVerifyClick}
                >
                  Verify you're not a robot
                </button>
              ) : (
                <div className="verify_input_container">
                  <p className="verify_prompt">PLEASE TYPE IN 'Verify'</p>
                  <input
                    type="text"
                    className="verify_input"
                    value={verifyInput}
                    onChange={handleVerifyInputChange}
                    placeholder="Type here..."
                  />
                  <button
                    className="verify_submit_button"
                    onClick={handleVerifySubmit}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Video Player */}
        {!loading && !error && isVerified && embedUrl && (
          <div className="watch_player_container">
            <iframe
              src={embedUrl}
              className="watch_player"
              allowFullScreen
              frameBorder="0"
              title="Video Player"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to fetch movie streaming link
async function fetchMovieStreamingLink(movieName, tmdbId) {
  // Step 1: Search for the movie
  const searchUrl = `https://ophim1.com/v1/api/tim-kiem?keyword=${encodeURIComponent(movieName)}`;
  const searchResponse = await fetch(searchUrl);
  const searchData = await searchResponse.json();

  // Step 2: Find the movie with matching TMDB ID
  const items = searchData?.data?.items || [];
  const targetMovie = items.find(
    (item) => item?.tmdb?.id && parseInt(item.tmdb.id) === parseInt(tmdbId)
  );

  if (!targetMovie) {
    throw new Error("Movie not found in streaming database");
  }

  const slug = targetMovie.slug;

  // Step 3: Get movie details
  const detailUrl = `https://ophim1.com/v1/api/phim/${slug}`;
  const detailResponse = await fetch(detailUrl);
  const detailData = await detailResponse.json();

  // Step 4: Get episodes data
  const episodes = detailData?.data?.item?.episodes || [];
  if (episodes.length === 0) {
    throw new Error("No episodes data available");
  }

  // Step 5: Get embed link
  const serverData = episodes[0]?.server_data || [];
  if (serverData.length === 0) {
    throw new Error("No streaming links available");
  }

  // Get the "full" episode or the first one
  let targetEpisode = serverData.find((ep) => ep.slug === "full");
  if (!targetEpisode) {
    targetEpisode = serverData[0];
  }

  const embedLink = targetEpisode.link_embed || targetEpisode.link_m3u8;
  if (!embedLink) {
    throw new Error("No embed link found");
  }

  return embedLink;
}

// Helper function to fetch TV show streaming link
async function fetchTvStreamingLink(showName, tmdbId, targetSeason, targetEpisode) {
  let slug = null;
  let currentPage = 1;
  let totalPages = 1;

  // Step 1 & 2: Search for the show with pagination support
  while (currentPage <= totalPages && !slug) {
    const searchUrl = `https://ophim1.com/v1/api/tim-kiem?keyword=${encodeURIComponent(showName)}&page=${currentPage}`;
    const searchResponse = await fetch(searchUrl);
    const searchData = await searchResponse.json();

    const items = searchData?.data?.items || [];
    const pagination = searchData?.data?.params?.pagination;

    // Check pagination
    if (pagination) {
      const totalItems = pagination.totalItems || 0;
      const itemsPerPage = pagination.totalItemsPerPage || 1;
      totalPages = Math.ceil(totalItems / itemsPerPage);
    }

    // Find the show with matching TMDB ID and season
    const targetShow = items.find(
      (item) =>
        item?.tmdb?.id &&
        parseInt(item.tmdb.id) === parseInt(tmdbId) &&
        item?.tmdb?.season &&
        parseInt(item.tmdb.season) === parseInt(targetSeason)
    );

    if (targetShow) {
      slug = targetShow.slug;
      break;
    }

    currentPage++;
  }

  if (!slug) {
    throw new Error("Show season not found in streaming database");
  }

  // Step 3: Get show details
  const detailUrl = `https://ophim1.com/v1/api/phim/${slug}`;
  const detailResponse = await fetch(detailUrl);
  const detailData = await detailResponse.json();

  // Step 4: Get episodes data
  const episodes = detailData?.data?.item?.episodes || [];
  if (episodes.length === 0) {
    throw new Error("No episodes data available");
  }

  // Step 5: Get embed link for specific episode
  const serverData = episodes[0]?.server_data || [];
  if (serverData.length === 0) {
    throw new Error("No streaming links available");
  }

  // Find the target episode
  const episodeData = serverData.find(
    (ep) => ep.slug === String(targetEpisode) || ep.name === String(targetEpisode)
  );

  if (!episodeData) {
    throw new Error(`Episode ${targetEpisode} not found`);
  }

  const embedLink = episodeData.link_embed || episodeData.link_m3u8;
  if (!embedLink) {
    throw new Error("No embed link found for this episode");
  }

  return { url: embedLink, episodes: serverData };
}